import { useLocation } from '@tanstack/react-location';
import clsx from 'clsx';
import React, {
  ChangeEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useFlexAssessmentContext } from '../../context/context';
import buttons from '../../css/buttons.module.scss';
import exitIntentStyles from '../../css/exitIntent.module.scss';
import styles from '../../css/modal.module.scss';
import { FlexAssessmentContextAction } from '../../types/flex-assessment-context-action';
import { NotificationType } from '../../types/notification-type';
import { PAGE_URLS } from '../../utils/constants';
import { MIXPANEL_EVENTS } from '../../utils/mixpanel-consts';
import { track } from '../../utils/mixpanel-helper';
import { STRINGS } from '../../utils/strings';
import { useExitIntent } from '../../utils/use-exit-intent';
import { Modal, useModalToggle } from './portal';

const LANDING_PAGE_EXIT_REASONS = [
  'I’m not interested',
  'Now isn’t a good time',
  'I didn’t find answers to my questions',
  'I didn’t find the website easy to use',
  'Something else',
];

const SIGNUP_PAGE_EXIT_REASONS = [
  'I changed my mind',
  'Don’t worry, I want to finish this later',
  'I didn’t find answers to my questions',
  'Concerns about the registration fields',
  'Something else',
];

const MAX_TEXT_LENGTH = 140;

const MAX_MODAL_APPEARANCES_PER_SESSION = 1;

const ExitIntentModal = React.memo((): JSX.Element => {
  const location = useLocation();
  const { dispatch } = useFlexAssessmentContext();
  const [selectedIdx, setSelectedIdx] = useState<undefined | number>();
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalAppearances, setModalAppearances] = useState(0);
  const { isOn: isExitIntentModalOn, toggle: toggleExitIntentModalOn } =
    useModalToggle();
  const { isTryingToExit, resetIntent } = useExitIntent();
  const options =
    location.current.pathname === PAGE_URLS.SIGNUP
      ? SIGNUP_PAGE_EXIT_REASONS
      : LANDING_PAGE_EXIT_REASONS;
  const isFreeTextSelected = selectedIdx === options.length - 1;
  const textArea = useRef<null | HTMLTextAreaElement>(null);
  const isCriteriaMet = selectedIdx !== undefined;

  useEffect(() => {
    if (isExitIntentModalOn) {
      setModalAppearances((prev) => prev + 1);
    }
  }, [isExitIntentModalOn]);

  useEffect(() => {
    if (
      isTryingToExit &&
      modalAppearances < MAX_MODAL_APPEARANCES_PER_SESSION &&
      !isSubmitting
    ) {
      track(MIXPANEL_EVENTS.EXIT_INTENT_SHOWING);
      toggleExitIntentModalOn();
    }
  }, [isTryingToExit]);

  useLayoutEffect(() => {
    if (isFreeTextSelected && textArea.current) {
      textArea.current?.focus();
    }
  }, [isFreeTextSelected, textArea]);

  useEffect(() => {
    if (isCriteriaMet && !isFreeTextSelected) {
      submit();
    }
  }, [isCriteriaMet, isFreeTextSelected]);

  const toggleSelection = (answerIdx: number): void => {
    setSelectedIdx(answerIdx);
  };

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
    const answerIdx = parseInt(event.target.value, 10);
    toggleSelection(answerIdx);
  };

  const onClickHandler = (answerIdx: number): void => {
    toggleSelection(answerIdx);
  };

  const onTextAreaChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (text.length < MAX_TEXT_LENGTH) {
      setText(event.target.value);
    }
  };

  const submit = () => {
    if (!isCriteriaMet) {
      return;
    }
    setIsSubmitting(true);
    track(MIXPANEL_EVENTS.EXIT_INTENT_REASON, {
      reason: options[selectedIdx],
      ...(isFreeTextSelected && { text }),
    });
    setSelectedIdx(undefined);
    setText('');
    exit();
    dispatch({
      type: FlexAssessmentContextAction.showNotification,
      payload: {
        type: NotificationType.Success,
        isShowing: true,
        message: STRINGS.SUCCESS.SAVED_FEEDBACK,
      },
    });
  };

  const exit = () => {
    track(MIXPANEL_EVENTS.EXIT_INTENT_CLOSED);
    toggleExitIntentModalOn();
    resetIntent();
  };

  return (
    <Modal isOpen={isExitIntentModalOn} onClose={exit}>
      <div className={styles.modalContent}>
        <h3 className={styles.modalTitle}>Sorry to see you go!</h3>
        <p className={styles.p}>
          What's your reason for leaving?{' '}
          <small className={styles.instructions}>
            Please select one option.
          </small>
        </p>
        <ul className={clsx(exitIntentStyles.options, styles.options)}>
          {options.map((answer, answerIdx) => {
            const isSelected: boolean = selectedIdx === answerIdx;
            return (
              <li key={answerIdx}>
                <label
                  className={clsx(exitIntentStyles.option, {
                    [exitIntentStyles.selected]: isSelected,
                  })}
                >
                  <input
                    onClick={() => onClickHandler(answerIdx)}
                    onChange={onChangeHandler}
                    name="exitIntent"
                    type="radio"
                    value={answerIdx}
                    checked={isSelected}
                  />
                  {answer}
                </label>
              </li>
            );
          })}
        </ul>
        {isFreeTextSelected && (
          <>
            <div className={styles.freeTextContainer}>
              <small>Enter your response</small>
              <textarea
                ref={textArea}
                rows={3}
                className={styles.textArea}
                value={text}
                onChange={onTextAreaChange}
              />
              <small
                className={clsx(styles.textLength, {
                  [styles.textAtFullLength]:
                    MAX_TEXT_LENGTH - text?.length < MAX_TEXT_LENGTH * 0.1,
                })}
              >
                {text.length} / {MAX_TEXT_LENGTH}
              </small>
            </div>
            <button
              onClick={submit}
              className={buttons.submitBtn}
              disabled={!isCriteriaMet}
            >
              Send
            </button>
          </>
        )}
      </div>
    </Modal>
  );
});

export default ExitIntentModal;
